import { useRouter } from "next/router";
import { useState, useEffect } from "react";
import { cn, Tooltip } from "ui";
import { getCarriedQueryParams } from "../utils/urlParamsAndFilters";
import Link, { type LinkProps } from "next/link";
import { InformationCircleIcon } from "icons/outline";

type Tab = { name: string; href: string; current: boolean; hint?: string };

export type HeaderTabsProps = {
  tabs: Array<Tab>;
  getTabLink?: (tabHref: string) => LinkProps["href"];
  className?: string;
};

export default function HeaderTabs({
  tabs,
  getTabLink,
  className,
}: HeaderTabsProps) {
  const router = useRouter();
  const { query, isReady } = router;
  const { hide_all_providers: hideAllProviders } = query;
  const [tabsList, setTabsList] = useState<Array<Tab>>([]);

  useEffect(() => {
    if (!isReady || !tabs?.length) return;

    if (String(hideAllProviders).toLocaleLowerCase() === "true") {
      setTabsList(tabs.filter((tab) => tab.href !== "/connections/available"));
    } else {
      setTabsList(tabs);
    }
  }, [hideAllProviders, isReady, tabs]);

  return (
    <div>
      <div className="sm:hidden">
        <label htmlFor="tabs" className="sr-only">
          Select a tab
        </label>
        {/* Use an "onChange" listener to redirect the user to the selected tab URL. */}
        <select
          id="tabs"
          name="tabs"
          className="my-2 block w-full rounded-md border-foundations-bold  bg-foundations-default py-2 pl-3 pr-10 text-base text-foundations-bolder placeholder:text-foundations-subtle focus:border-info-bold focus:outline-none focus:ring-info-bold sm:text-sm"
          value={tabsList.find((tab) => tab.current)?.href}
          onChange={(e) => {
            router.push({
              pathname: e.target.value,
              query: { ...getCarriedQueryParams(query) },
            });
          }}
        >
          {tabsList.map((tab) => (
            <option key={tab.name} value={tab.href}>
              {tab.name}
            </option>
          ))}
        </select>
      </div>
      <div className="hidden sm:block">
        <div className={cn("border-b border-foundations-subtle", className)}>
          <nav className="-mb-px flex space-x-4" aria-label="Tabs">
            {tabsList.map((tab) => (
              <Link
                key={tab.name}
                href={
                  getTabLink?.(tab.href) || {
                    pathname: tab.href,
                    query: { ...getCarriedQueryParams(query) },
                  }
                }
                className={cn(
                  tab.current
                    ? "border-info-bold text-foundations-bolder"
                    : "border-transparent text-foundations-subtle hover:border-foundations-bold hover:text-foundations-subtle",
                  "flex cursor-pointer items-center gap-1.5 whitespace-nowrap border-b-2 px-2 py-2.5 text-sm font-medium first-of-type:pl-0"
                )}
                aria-current={tab.current ? "page" : undefined}
              >
                {tab.name}
                {tab.hint && (
                  <Tooltip content={tab.hint} variant="light">
                    <div className="h-5 w-5 p-0.5">
                      <InformationCircleIcon className="h-4 w-4 rounded-full bg-foundations-subtlest text-foundations-subtler" />
                    </div>
                  </Tooltip>
                )}
              </Link>
            ))}
          </nav>
        </div>
      </div>
    </div>
  );
}
